import Link from "next/link";
import Head from "next/head";
import NextScript from "next/script";

import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Which events are included in the Young Alumni Pass?",
    answer:
      "All non-conference home football games (excluding South Carolina), most men's basketball and baseball games and all women's basketball, women's gymnastics, softball and men's soccer. You can also secure a seat for home conference football games and South Carolina games, as well as premier basketball and baseball games, for an additional fee.",
  },
  {
    question: "How do I manage my reservations?",
    answer:
      'After purchasing your Young Alumni Pass, you can navigate to Upcoming Games and select "Reserve" next to the game you want to attend. Please note that you can hold and manage up to three (3) reservations at a time.',
  },
  {
    question: "How many games can I reserve at a time?",
    answer:
      "You may hold and manage up to three (3) different game reservations at a time. Once a reserved game passes, you'll be able to make a new reservation.",
  },
  {
    question: "How much does the Young Alumni Pass cost?",
    answer:
      "The Young Alumni Pass is available as a monthly subscription at $24.99 per month (August through May) or as an annual plan at $249.",
  },
  {
    question: "When can I purchase the Young Alumni Pass?",
    answer:
      "Purchase can happen any time through May. Beginning in August, you'll also be able to purchase upsell access to non-included games.",
  },
  {
    question: "What if I can't attend a game I reserved?",
    answer:
      "If you are unable to attend a reserved game, please cancel the reservation as early as possible so the seat can be made available to other pass holders.",
  },
  {
    question: "Can I get a refund for the Young Alumni Pass?",
    answer:
      "Young Alumni Pass purchases are non-refundable. For questions regarding your subscription, please reach out to Clemson Athletics support.",
  },
  {
    question: "Can I share my Young Alumni Pass with someone else?",
    answer:
      "The Young Alumni Pass is non-transferable and tied to your account. Guest access may be available for select games through additional ticket purchases.",
  },
  {
    question: "Who qualifies for the Young Alumni Pass?",
    answer:
      "If you are currently a IPTAY Young Alumni donor, you are eligible for the Young Alumni discount. If you are not currently an IPTAY Young Alumni donor but graduated from Clemson within the last four years, please contact IPTAY at 864-656-2115 to join the Young Alumni program and become eligible for the Young Alumni discount.",
  },
];

export const REGISTER_TERMS_AND_CONDITION = () => (
  <>
    By creating an account, you agree to receive account-related notification
    and marketing communication from Clemson University and you confirm that you
    have read and agree to the platform&apos;s{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    .
  </>
);

export const TERMS_AND_CONDITION = () => (
  <>
    By checking the box you agree to receive notifications regarding your
    purchase and to the{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    .
  </>
);

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      {/* Meta Pixel */}
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '1411668867137634');
            fbq('track', 'PageView');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try{
                const { cartRes, checkoutRes, passes } = e.detail;

                let content_name = "";
                if(cartRes.products && cartRes.products.length) {
                  content_name = "${TEAM_SCHEMA}_single_game_purchases";
                };
                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_name = passDetail.passTitle;
                };

                fbq('track', 'Purchase', {
                  content_name: content_name,
                  value: String(cartRes.totalAmount),
                  currency: 'USD',
                  order_id: checkoutRes.orderNumber,
                });
                console.log('meta pixel purchase event fired.');

              } catch(error){
                console.log('meta pixel purchase event error:', error);
              }
            });
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1411668867137634&ev=PageView&noscript=1"
        />
      </noscript>

      {/* Google Analytics 4 */}
      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=G-Y3KHDXKEW0"
      />
      <script
        id={`${TEAM_SCHEMA}-analytics-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'G-Y3KHDXKEW0');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try {
                const { cartRes, checkoutRes } = e.detail;
                gtag('event', 'purchase', {
                  'send_to': 'G-Y3KHDXKEW0',
                  'value': cartRes.totalAmount,
                  'currency': 'USD',
                  'transaction_id': checkoutRes.orderNumber
                });
                console.log('gtag analytics purchase event fired.');
              } catch (error) {
                console.log('gtag analytics purchase event error:', error);
              }
            });
          `,
        }}
      />
    </Head>
  );
};
